import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AcModel } from '../../../models/ac.model';
import { SlotModel } from '../../../models/slot.model';
import moment, { Moment } from 'moment';
import { ExerciseModel } from '../../../models/exercise.model';
import { DataService } from '../../../services/data.service';
import { Subscription } from 'rxjs';
import { TimezoneService } from '../../../services/timezone.service';
import { AuthStateService } from '../../../services/auth-state.service';
import { v4 as uuidv4 } from 'uuid';

export enum ExerciseContainerType {
  Exercise = 'exercise',
  Org = 'org',
  Feedback = 'feedback',
  Onlinetest = 'onlinetest',
  Conference = 'conference',
  Break = 'break'
}

export interface ExerciseContainer {
  id: number;
  type: ExerciseContainerType;
  name: string;
  slots: Array<SlotModel>;
  exercise?: ExerciseModel;
  time?: Moment;
  duration?: number;
  part?: number;
  opened?: boolean;
}

@Component({
  selector: 'app-agenda',
  templateUrl: './agenda.component.html',
  styleUrls: ['./agenda.component.scss']
})
export class AgendaComponent implements OnInit, OnDestroy {

  public ac: AcModel = null;
  public containers: Array<ExerciseContainer> = [];
  public acSubscription: Subscription = null;
  public acStatusSubscription: Subscription = null;
  public showProcedureTimes = this.timezone.getShowProcedureTimes();

  constructor(
    private data: DataService,
    private auth: AuthStateService,
    public timezone: TimezoneService
  ) { }

  ngOnInit(): void {
    this.acSubscription = this.data.ac$.subscribe({
      next: (ac: AcModel) => {
        if (ac !== null) {
          this.ac = ac;
          this.unsubscribeStatus();
          this.acStatusSubscription = this.ac.acStatus$.subscribe({
            next: () => {
              this.adjustBreaks();
            }
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.acSubscription !== null) {
      this.acSubscription.unsubscribe();
    }
    this.unsubscribeStatus();
  }

  public onChangeShowProcedureTimes(event: any): void {
    this.timezone.setShowProcedureTimes(event.checked);
  }

  private adjustBreaks(): void {
    if (this.ac?.isSingle() && !this.auth.isModerator()) {
      const isCandidate = this.auth.isCandidate();
      const isObserver = this.auth.isObserver();
      // delete not relevant containers
      this.containers = this.ac?.containers.filter((container: ExerciseContainer) => {
        return container.slots.filter((slot: SlotModel) => {
          return (slot.for_candidate && isCandidate) ||
            (slot.for_observer && isObserver);
        }).length > 0;
      });
      // delete all breaks before set up pseudo breaks
      this.containers = this.containers.filter((container: ExerciseContainer) => {
        return container.type !== ExerciseContainerType.Break;
      });
      // add pseudo breaks
      const containers = [];
      this.containers.forEach((container: ExerciseContainer, index: number) => {
        containers.push(container);
        if (index < this.containers.length - 1) {
          const actualEnd = moment(container.time).add(container.duration, 'minutes');
          const nextContainer = this.containers[index + 1] || null;
          if (nextContainer?.time) {
            const nextStart = moment(nextContainer.time);
            const diff = nextStart.diff(actualEnd, 'minutes');
            if (diff > 0) {
              const newSlot = new SlotModel().deserialize({
                id: uuidv4(),
                type: 'break',
                name: 'break',
                start_time: 0,
                duration: diff,
                for_candidate: isCandidate,
                for_observer: isObserver
              });
              containers.push({
                id: uuidv4(),
                type: ExerciseContainerType.Break,
                name: 'break',
                slots: [newSlot],
                time: actualEnd,
                duration: diff
              });
            }
          }
        }
      });
      this.containers = containers;
    } else {
      this.containers = this.ac?.containers;
    }
  }

  private unsubscribeStatus(): void {
    if (this.acStatusSubscription !== null) {
      this.acStatusSubscription.unsubscribe();
    }
  }

}
